import React, { useState, useEffect } from "react";
import { auth, firestore, database } from "../../firebaseConfig";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
} from "firebase/firestore";
import {
  ref,
  runTransaction,
  onValue,
} from "firebase/database";
import toast from "react-hot-toast";
import {
  Image,
  User,
  Phone,
  Mail,
  Edit2,
  X,
  Eye,
  FileText,
  Users,
  DollarSign,
  Calendar,
  ChevronRight,
  Check,
  Loader2,
  Lock,
  Save,
  ChevronLeft,
  CheckCircle,
  Download,
  Columns,
} from "lucide-react";
import { API_BASE_URL } from "../../utils/apiBase";

// Import layouts for different user roles
import FarmerLayout from "../../components/layouts/FarmerLayout";
import ClientLayout from "../../components/layouts/ClientLayout";
import CompanyLayout from "../../components/layouts/CompanyLayout";

// Constants
const PAPER_TYPES = {
  SINGLE_DAY: "single_day",
  WEEKLY: "weekly",
  HALF_MONTHLY: "half_monthly",
};

// Annotation payment rates (in KES)
const ANNOTATION_RATES = {
  [PAPER_TYPES.SINGLE_DAY]: 15,
  [PAPER_TYPES.WEEKLY]: 60,
  [PAPER_TYPES.HALF_MONTHLY]: 130,
};

// Days of week for weekly papers
const DAYS_OF_WEEK = ["Saturday", "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

// Helper function to extract image URL from paper object
const getImageUrl = (paper) => {
  return paper?.uploads?.[0]?.images?.[0]?.url || null;
};

/**
 * AnnotationMarketplace
 * - Allows users (Farmer, Client, Company) to view and annotate milk journal images
 * - Users must provide phone number for identification
 * - Papers are displayed with blurred sensitive info
 * - Users can bid on papers and input data based on paper type
 * - Wraps content in appropriate layout based on user role
 */
const AnnotationMarketplace = () => {
  // User state
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [userRole, setUserRole] = useState(null); // Farmer, Client, Company, Employee
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);

  // Papers state
  const [availablePapers, setAvailablePapers] = useState([]);
  const [loadingPapers, setLoadingPapers] = useState(true);

  // Selected paper and annotation state
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [showAnnotationModal, setShowAnnotationModal] = useState(false);
  const [annotationData, setAnnotationData] = useState({});
  const [currentFarmerIndex, setCurrentFarmerIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [farmerMappings, setFarmerMappings] = useState([]);
  const [loadingMappings, setLoadingMappings] = useState(false);

  // Image preview state
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState("");

  // Privacy blur and download state
  const [blurredImageUrl, setBlurredImageUrl] = useState("");
  const [processingBlur, setProcessingBlur] = useState(false);
  const [sideBySideMode, setSideBySideMode] = useState(false);


  // Image zoom/pan state
  const [imageScale, setImageScale] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Load current user
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        await loadUserData(currentUser.uid);
      } else {
        setUserData(null);
        setPhoneNumber("");
        setUserRole(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Load user data from Firestore
  const loadUserData = async (uid) => {
    try {
      const userDoc = await getDoc(doc(firestore, "users", uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(data);

        // Determine user role from accountType (normalize to lowercase for comparison)
        const accountType = (data.accountType || data.role || "").toLowerCase();
        const roleMapping = {
          farmer: "Farmer",
          client: "Client",
          milkseller: "Client",
          company: "Company",
          companyadmin: "Company",
          employee: "Employee",
        };
        setUserRole(roleMapping[accountType] || null);

        // Check if user has phone number
        if (data.phoneNumber) {
          setPhoneNumber(data.phoneNumber);
          setIsFirstTimeUser(false);
        } else {
          setIsFirstTimeUser(true);
          setShowPhoneModal(true);
        }
      }
    } catch (error) {
      console.error("Error loading user data:", error);
      toast.error("Failed to load user profile");
    }
  };

  // Load available papers for annotation from GlobalAnnotationQueue
  useEffect(() => {
    if (!user) return;

    setLoadingPapers(true);

    // Subscribe to GlobalAnnotationQueue for real-time updates
    const globalQueueRef = ref(database, "GlobalAnnotationQueue");

    const handleQueueUpdate = async (snapshot) => {
      try {
        if (!snapshot.exists()) {
          setAvailablePapers([]);
          setLoadingPapers(false);
          return;
        }

        const queueData = snapshot.val();
        const papers = [];

        // Process each paper in the global queue
        for (const [paperId, paperData] of Object.entries(queueData)) {
          // Only show papers that are pending annotation (not completed)
          if (paperData.status !== "pending_annotation") {
            continue;
          }

          // Get bidder count from Firestore for compatibility
          let bidCount = 0;
          try {
            const bidsRef = collection(firestore, "milk_journals", paperId, "bids");
            const bidsSnap = await getDocs(bidsRef);
            bidCount = bidsSnap.size;
          } catch (error) {
            // If bids collection doesn't exist, use openedCount as fallback
            console.warn("Bids collection not found for paper:", paperId, error);
            bidCount = paperData.openedCount || 0;
          }

          papers.push({
            id: paperId,
            companyName: paperData.companyName,
            paperType: paperData.paperType,
            dateRange: paperData.dateRange,
            totalFarmers: paperData.totalFarmers,
            totalPages: paperData.totalPages,
            status: paperData.status,
            uploadedAt: paperData.uploadedAt,
            // GlobalAnnotationQueue specific fields
            openedCount: paperData.openedCount || 0,
            completedCount: paperData.completedCount || 0,
            imageCount: paperData.imageCount || 0,
            // Map images from GlobalAnnotationQueue to match expected format
            uploads: [{
              images: paperData.images || [],
              status: paperData.status,
            }],
            bidCount: bidCount,
          });
        }

        // Sort by uploadedAt (most recent first), papers without timestamps go to the end
        papers.sort((a, b) => {
          const dateA = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
          const dateB = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
          return dateB - dateA;
        });

        setAvailablePapers(papers);
      } catch (error) {
        console.error("Error loading papers from GlobalAnnotationQueue:", error);
        toast.error("Failed to load available papers");
      } finally {
        setLoadingPapers(false);
      }
    };

    const unsubscribe = onValue(globalQueueRef, handleQueueUpdate);

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [user]);

  // Save phone number
  const handleSavePhone = async () => {
    if (!phoneInput.trim()) {
      toast.error("Please enter a valid phone number");
      return;
    }

    // Basic phone validation (Kenyan format - supports Safaricom 7xx, Airtel 7xx/1xx)
    const phoneRegex = /^(\+254|0)?[1-9]\d{8}$/;
    const cleanedPhone = phoneInput.replace(/\s+/g, "");

    if (!phoneRegex.test(cleanedPhone)) {
      toast.error("Please enter a valid Kenyan phone number (e.g., 0712345678)");
      return;
    }

    setSavingPhone(true);
    try {
      // Format phone number consistently - only format if it looks like a Kenyan number
      let formattedPhone = cleanedPhone;
      if (cleanedPhone.startsWith("0") && cleanedPhone.length === 10) {
        formattedPhone = "+254" + cleanedPhone.slice(1);
      } else if (cleanedPhone.startsWith("+254")) {
        formattedPhone = cleanedPhone;
      } else if (cleanedPhone.length === 9) {
        // Assume it's a Kenyan number without prefix
        formattedPhone = "+254" + cleanedPhone;
      }

      await updateDoc(doc(firestore, "users", user.uid), {
        phoneNumber: formattedPhone,
      });

      setPhoneNumber(formattedPhone);
      setShowPhoneModal(false);
      setIsFirstTimeUser(false);
      toast.success("Phone number saved successfully");
    } catch (error) {
      console.error("Error saving phone:", error);
      toast.error("Failed to save phone number");
    } finally {
      setSavingPhone(false);
    }
  };

  // Get paper type label
  const getPaperTypeLabel = (type) => {
    switch (type) {
      case PAPER_TYPES.SINGLE_DAY:
        return "Single Day";
      case PAPER_TYPES.WEEKLY:
        return "Weekly (7 days)";
      case PAPER_TYPES.HALF_MONTHLY:
        return "Half Monthly (15 days)";
      default:
        return type;
    }
  };

  // Get date columns based on paper type
  const getDateColumns = (paper) => {
    if (paper.paperType === PAPER_TYPES.SINGLE_DAY) {
      return ["Day 1"];
    } else if (paper.paperType === PAPER_TYPES.WEEKLY) {
      return DAYS_OF_WEEK;
    } else if (paper.paperType === PAPER_TYPES.HALF_MONTHLY) {
      return Array.from({ length: 15 }, (_, i) => `Day ${i + 1}`);
    }
    return [];
  };

  // Load farmer mappings for a paper
  const loadFarmerMappings = async (paperId) => {
    setLoadingMappings(true);
    try {
      const mappingsRef = collection(firestore, "milk_journals", paperId, "farmer_mappings");
      const mappingsSnap = await getDocs(mappingsRef);

      const mappings = mappingsSnap.docs
        .map((doc) => doc.data())
        .sort((a, b) => a.rowId - b.rowId);

      setFarmerMappings(mappings);

      // Initialize annotation data structure
      const initialData = {};
      mappings.forEach((m) => {
        initialData[m.rowId] = {};
      });
      setAnnotationData(initialData);

    } catch (error) {
      console.error("Error loading farmer mappings:", error);
      toast.error("Failed to load paper data");
    } finally {
      setLoadingMappings(false);
    }
  };

  // Handle opening a paper for annotation (bid)
  const handleOpenPaper = async (paper) => {
    if (!phoneNumber) {
      toast.error("Please add your phone number first");
      setShowPhoneModal(true);
      return;
    }

    // Check if user already has an active bid on this paper
    try {
      const bidRef = doc(firestore, "milk_journals", paper.id, "bids", user.uid);
      const bidSnap = await getDoc(bidRef);

      const isNewBid = !bidSnap.exists();

      if (isNewBid) {
        // Create bid record
        await setDoc(bidRef, {
          userId: user.uid,
          phoneNumber: phoneNumber,
          email: user.email,
          fullName: userData?.fullName || "Unknown",
          bidTime: new Date().toISOString(),
          status: "active",
        });

        // Update global annotation queue - increment openedCount and track user
        const globalQueueRef = ref(database, `GlobalAnnotationQueue/${paper.id}`);
        await runTransaction(globalQueueRef, (currentData) => {
          if (currentData === null) {
            // Initialize data structure if it doesn't exist (paper was not uploaded via the new flow)
            // Include images from uploads if available
            const images = paper.uploads?.[0]?.images || [];
            return {
              paperId: paper.id,
              companyName: paper.companyName,
              paperType: paper.paperType,
              status: "pending_annotation",
              images: images.map((img) => ({
                fileName: img.fileName,
                url: img.url,
                storagePath: img.storagePath,
              })),
              openedCount: 1,
              completedCount: 0,
              openedBy: {
                [user.uid]: {
                  userId: user.uid,
                  phoneNumber: phoneNumber,
                  fullName: userData?.fullName || "Unknown",
                  openedAt: new Date().toISOString(),
                },
              },
              completedBy: {},
              // initialize completedSubmissionIds so other files can reference them
              completedSubmissionIds: [],
            };
          }
          return {
            ...currentData,
            openedCount: (currentData.openedCount || 0) + 1,
            openedBy: {
              ...(currentData.openedBy || {}),
              [user.uid]: {
                userId: user.uid,
                phoneNumber: phoneNumber,
                fullName: userData?.fullName || "Unknown",
                openedAt: new Date().toISOString(),
              },
            },
          };
        });
      }

      setSelectedPaper(paper);
      await loadFarmerMappings(paper.id);
      setShowAnnotationModal(true);
      setCurrentFarmerIndex(0);

    } catch (error) {
      console.error("Error opening paper:", error);
      toast.error("Failed to open paper");
    }
  };

  // Handle data input change
  const handleDataChange = (farmerId, dayIndex, value) => {
    setAnnotationData((prev) => ({
      ...prev,
      [farmerId]: {
        ...prev[farmerId],
        [dayIndex]: value,
      },
    }));
  };

  // Navigate to next farmer
  const handleNextFarmer = () => {
    if (currentFarmerIndex < farmerMappings.length - 1) {
      setCurrentFarmerIndex((prev) => prev + 1);
    }
  };

  // Navigate to previous farmer
  const handlePrevFarmer = () => {
    if (currentFarmerIndex > 0) {
      setCurrentFarmerIndex((prev) => prev - 1);
    }
  };

  // Submit annotation
  const handleSubmitAnnotation = async () => {
    if (!selectedPaper || !user) return;

    // Validate that at least some data has been entered (allow zero values)
    const dateColumns = getDateColumns(selectedPaper);
    let hasData = false;

    for (const farmerId of Object.keys(annotationData)) {
      for (let dayIdx = 0; dayIdx < dateColumns.length; dayIdx++) {
        const value = annotationData[farmerId]?.[dayIdx];
        if (value !== undefined && value !== "" && !isNaN(parseFloat(value))) {
          hasData = true;
          break;
        }
      }
      if (hasData) break;
    }

    if (!hasData) {
      toast.error("Please enter at least some data before submitting");
      return;
    }

    setSubmitting(true);
    try {
      const timestamp = new Date().toISOString();

      // Create annotation submission document
      const submissionData = {
        paperId: selectedPaper.id,
        companyName: selectedPaper.companyName,
        paperType: selectedPaper.paperType,
        dateRange: selectedPaper.dateRange,
        annotatorId: user.uid,
        annotatorPhone: phoneNumber,
        annotatorEmail: user.email,
        annotatorName: userData?.fullName || "Unknown",
        annotations: annotationData,
        farmerCount: farmerMappings.length,
        status: "pending_review", // pending_review, accepted, declined
        submittedAt: timestamp,
        totalFarmers: selectedPaper.totalFarmers,
      };

      // Save to annotation_queue collection in Firestore
      const queueRef = doc(collection(firestore, "annotation_queue"));
      await setDoc(queueRef, {
        ...submissionData,
        id: queueRef.id,
      });

      // Update user's bid status
      await updateDoc(doc(firestore, "milk_journals", selectedPaper.id, "bids", user.uid), {
        status: "submitted",
        submittedAt: timestamp,
        submissionId: queueRef.id,
      });

      // Update global annotation queue - increment completedCount and track user
      const globalQueueRef = ref(database, `GlobalAnnotationQueue/${selectedPaper.id}`);
      await runTransaction(globalQueueRef, (currentData) => {
        if (currentData === null) {
          // Initialize data structure if it doesn't exist (paper was not uploaded via the new flow)
          // Include images from uploads if available
          const images = selectedPaper.uploads?.[0]?.images || [];
          return {
            paperId: selectedPaper.id,
            companyName: selectedPaper.companyName,
            paperType: selectedPaper.paperType,
            status: "pending_annotation",
            images: images.map((img) => ({
              fileName: img.fileName,
              url: img.url,
              storagePath: img.storagePath,
            })),
            openedCount: 1,  // User must have opened it to complete it
            completedCount: 1,
            openedBy: {},
            completedBy: {
              [user.uid]: {
                userId: user.uid,
                phoneNumber: phoneNumber,
                fullName: userData?.fullName || "Unknown",
                completedAt: timestamp,
                submissionId: queueRef.id,
              },
            },
            // NEW: store completed submission ids so other parts of the app can reference them
            completedSubmissionIds: [queueRef.id],
          };
        }

        // existing node -> update counts, completedBy map and append submission id(s)
        const updatedCompletedBy = {
          ...(currentData.completedBy || {}),
          [user.uid]: {
            userId: user.uid,
            phoneNumber: phoneNumber,
            fullName: userData?.fullName || "Unknown",
            completedAt: timestamp,
            submissionId: queueRef.id,
          },
        };

        // Ensure we have an array of completed submission ids and append without duplicates
        const existingIds = Array.isArray(currentData.completedSubmissionIds) ? currentData.completedSubmissionIds.slice() : [];
        if (!existingIds.includes(queueRef.id)) {
          existingIds.push(queueRef.id);
        }

        return {
          ...currentData,
          completedCount: (currentData.completedCount || 0) + 1,
          completedBy: updatedCompletedBy,
          completedSubmissionIds: existingIds,
        };
      });

      toast.success("Annotation submitted successfully! Waiting for review.");
      setShowAnnotationModal(false);
      setSelectedPaper(null);
      setAnnotationData({});
      setCurrentFarmerIndex(0);

    } catch (error) {
      console.error("Error submitting annotation:", error);
      toast.error("Failed to submit annotation");
    } finally {
      setSubmitting(false);
    }
  };

  // View image with blurred content
  const handleViewImage = (imageUrl) => {
    setPreviewImageUrl(imageUrl);
    setShowImagePreview(true);
  };

  // Fetch image and convert to base64
  const fetchImageAsBase64 = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Failed to read image data"));
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error fetching image:", error);
      throw new Error(`Could not load image: ${error.message}`);
    }
  };

  // Process image with privacy blur via backend
  const processPrivacyBlur = async (imageUrl) => {
    if (!user) {
      toast.error("Please log in to process images");
      return null;
    }

    if (!imageUrl) {
      toast.error("No image URL provided");
      return null;
    }

    setProcessingBlur(true);
    try {
      // Get auth token
      const idToken = await user.getIdToken();

      // Fetch image and convert to base64
      const imageBase64 = await fetchImageAsBase64(imageUrl);

      // Call Django API for privacy blur
      const response = await fetch(`${API_BASE_URL}/api/blur-privacy-info/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          image_base64: imageBase64,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to process image");
      }

      return result.blurred_image_base64;
    } catch (error) {
      console.error("Privacy blur error:", error);
      toast.error(`Failed to process image: ${error.message}`);
      return null;
    } finally {
      setProcessingBlur(false);
    }
  };

  // Handle download of blurred image
  const handleDownloadBlurred = async (imageUrl, paperId) => {
    const blurredImage = await processPrivacyBlur(imageUrl);
    if (blurredImage) {
      // Create download link
      const link = document.createElement("a");
      link.href = blurredImage;
      link.download = `annotation_paper_${paperId}_blurred.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Blurred image downloaded successfully");
    }
  };

  // Handle opening paper with blurred preview for side-by-side mode
  const handleOpenWithBlur = async (paper) => {
    if (!phoneNumber) {
      toast.error("Please add your phone number first");
      setShowPhoneModal(true);
      return;
    }

    const imageUrl = getImageUrl(paper);
    if (imageUrl) {
      // Images from GlobalAnnotationQueue are already privacy-blurred
      // Set the URL directly instead of re-processing
      setBlurredImageUrl(imageUrl);
    }

    // Reset zoom and pan when opening new paper
    setImageScale(1);
    setImagePosition({ x: 0, y: 0 });

    // Open the paper normally
    handleOpenPaper(paper);
    setSideBySideMode(true);
  };

  // Handle mouse wheel zoom
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY * -0.001;
    const newScale = Math.min(Math.max(0.5, imageScale + delta), 5);
    setImageScale(newScale);
  };

  // Handle mouse down for drag
  const handleMouseDown = (e) => {
    if (imageScale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
    }
  };

  // Handle mouse move for drag
  const handleMouseMove = (e) => {
    if (isDragging && imageScale > 1) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  // Handle mouse up to stop drag
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Reset zoom and pan
  const handleResetZoom = () => {
    setImageScale(1);
    setImagePosition({ x: 0, y: 0 });
  };

  // Current farmer being annotated
  const currentFarmer = farmerMappings[currentFarmerIndex];
  const dateColumns = selectedPaper ? getDateColumns(selectedPaper) : [];

  // Determine layout wrapper based on user role
  const getLayoutWrapper = (children) => {
    switch (userRole) {
      case "Farmer":
        return <FarmerLayout>{children}</FarmerLayout>;
      case "Client":
        return <ClientLayout>{children}</ClientLayout>;
      case "Company":
      case "Employee":
        return <CompanyLayout>{children}</CompanyLayout>;
      default:
        // Return without layout wrapper if role not determined yet
        return children;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-cyan-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Standalone page styling (when no layout wrapper is used)
  const standalonePageClass = "min-h-screen bg-gradient-to-br from-cyan-50 to-white";

  // Main content (layout wrapper handles outer styling when userRole is set)
  const content = (
    <div className={userRole ? undefined : standalonePageClass}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center">
                <Image className="text-white" size={20} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Annotation Marketplace</h1>
                <p className="text-sm text-gray-500">Earn by digitizing milk records</p>
              </div>
            </div>

            {/* Phone number edit button - prominent at top corner */}
            <button
              onClick={() => {
                setPhoneInput(phoneNumber || "");
                setShowPhoneModal(true);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
                phoneNumber
                  ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  : "bg-cyan-600 text-white hover:bg-cyan-700 animate-pulse"
              }`}
              title={phoneNumber ? "Edit phone number" : "Add phone number to start"}
            >
              <Phone size={18} />
              {phoneNumber ? (
                <>
                  <span className="hidden sm:inline">{phoneNumber}</span>
                  <Edit2 size={14} />
                </>
              ) : (
                <span>Add Phone</span>
              )}
            </button>
          </div>

          {/* User info bar */}
          {userData && (
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <User size={16} className="text-gray-400" />
                <span className="font-medium text-gray-700">{userData.fullName}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail size={16} className="text-gray-400" />
                <span className="text-gray-600">{user.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone size={16} className="text-gray-400" />
                <span className="text-gray-600">
                  {phoneNumber || (
                    <button
                      onClick={() => setShowPhoneModal(true)}
                      className="text-cyan-600 hover:underline"
                    >
                      Add phone number
                    </button>
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Payment rates info */}
        <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl p-4 mb-6 text-white">
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <DollarSign size={18} />
            Annotation Rates
          </h3>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="bg-white/20 rounded-lg p-2 text-center">
              <p className="font-bold">KES {ANNOTATION_RATES[PAPER_TYPES.SINGLE_DAY]}</p>
              <p className="text-xs opacity-80">Single Day</p>
            </div>
            <div className="bg-white/20 rounded-lg p-2 text-center">
              <p className="font-bold">KES {ANNOTATION_RATES[PAPER_TYPES.WEEKLY]}</p>
              <p className="text-xs opacity-80">Weekly</p>
            </div>
            <div className="bg-white/20 rounded-lg p-2 text-center">
              <p className="font-bold">KES {ANNOTATION_RATES[PAPER_TYPES.HALF_MONTHLY]}</p>
              <p className="text-xs opacity-80">Half Monthly</p>
            </div>
          </div>
        </div>

        {/* Available papers */}
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <FileText size={20} />
            Available Papers ({availablePapers.length})
          </h2>
        </div>

        {loadingPapers ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-cyan-600 animate-spin" />
          </div>
        ) : availablePapers.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Papers Available</h3>
            <p className="text-gray-500">
              Check back later for new annotation opportunities.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {availablePapers.map((paper) => (
              <div
                key={paper.id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition"
              >
                {/* Paper image preview - images from GlobalAnnotationQueue are already privacy-blurred */}
                {getImageUrl(paper) && (
                  <div className="relative h-40 bg-gray-100">
                    <img
                      src={getImageUrl(paper)}
                      alt="Paper preview"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                      <div className="bg-green-500/90 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
                        <Lock size={12} />
                        Privacy Protected
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-4">
                  {/* Company name - already protected in image, shown lightly blurred for text */}
                  <div className="text-sm text-gray-500 mb-2 flex items-center gap-1">
                    <span className="blur-[8px] select-none">{paper.companyName}</span>
                    <Lock size={12} className="text-gray-400" />
                  </div>

                  {/* Paper details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 flex items-center gap-1">
                        <Calendar size={14} />
                        Type
                      </span>
                      <span className="font-medium text-gray-800">
                        {getPaperTypeLabel(paper.paperType)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 flex items-center gap-1">
                        <Users size={14} />
                        Farmers
                      </span>
                      <span className="font-medium text-gray-800">
                        {paper.totalFarmers}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 flex items-center gap-1">
                        <DollarSign size={14} />
                        Rate
                      </span>
                      <span className="font-bold text-cyan-700">
                        KES {ANNOTATION_RATES[paper.paperType]}
                      </span>
                    </div>

                    {/* GlobalAnnotationQueue progress data */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 flex items-center gap-1">
                        <Eye size={14} />
                        Opened
                      </span>
                      <span className="font-medium text-blue-600">
                        {paper.openedCount || 0} user{(paper.openedCount || 0) !== 1 ? "s" : ""}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 flex items-center gap-1">
                        <CheckCircle size={14} />
                        Completed
                      </span>
                      <span className="font-medium text-green-600">
                        {paper.completedCount || 0} submission{(paper.completedCount || 0) !== 1 ? "s" : ""}
                      </span>
                    </div>

                    {paper.bidCount > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Bidders</span>
                        <span className="font-medium text-orange-600">
                          {paper.bidCount} user{paper.bidCount > 1 ? "s" : ""}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="space-y-2">
                    {/* Primary row: Preview and Open */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewImage(getImageUrl(paper))}
                        className="flex-1 py-2 px-3 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition flex items-center justify-center gap-1"
                        disabled={!getImageUrl(paper)}
                      >
                        <Eye size={16} />
                        Preview
                      </button>
                      <button
                        onClick={() => handleOpenPaper(paper)}
                        className="flex-1 py-2 px-3 bg-cyan-600 text-white rounded-lg text-sm font-medium hover:bg-cyan-700 transition flex items-center justify-center gap-1"
                      >
                        Open
                        <ChevronRight size={16} />
                      </button>
                    </div>

                    {/* Secondary row: Download and Side-by-Side */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDownloadBlurred(getImageUrl(paper), paper.id)}
                        className="flex-1 py-2 px-3 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition flex items-center justify-center gap-1"
                        disabled={!getImageUrl(paper) || processingBlur}
                      >
                        {processingBlur ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Download size={14} />
                            Download
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleOpenWithBlur(paper)}
                        className="flex-1 py-2 px-3 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-200 transition flex items-center justify-center gap-1"
                        disabled={!getImageUrl(paper)}
                        title="Open with side-by-side image preview (best for larger screens)"
                      >
                        <Columns size={14} />
                        Side-by-Side
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Phone Number Modal */}
      {showPhoneModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  {isFirstTimeUser ? "Add Your Phone Number" : "Edit Phone Number"}
                </h3>
                {!isFirstTimeUser && (
                  <button
                    onClick={() => setShowPhoneModal(false)}
                    className="p-1 hover:bg-gray-100 rounded-full"
                  >
                    <X size={20} className="text-gray-500" />
                  </button>
                )}
              </div>

              {isFirstTimeUser && (
                <p className="text-sm text-gray-600 mb-4">
                  To participate in annotation tasks, please provide your phone number.
                  This will be used for payment processing.
                </p>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="tel"
                    value={phoneInput}
                    onChange={(e) => setPhoneInput(e.target.value)}
                    placeholder="+254 7XX XXX XXX"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter your M-Pesa registered phone number
                </p>
              </div>

              <button
                onClick={handleSavePhone}
                disabled={savingPhone || !phoneInput.trim()}
                className={`w-full py-3 rounded-lg font-medium transition flex items-center justify-center gap-2 ${
                  savingPhone || !phoneInput.trim()
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-cyan-600 text-white hover:bg-cyan-700"
                }`}
              >
                {savingPhone ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Save Phone Number
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal - images from GlobalAnnotationQueue are already privacy-blurred */}
      {showImagePreview && previewImageUrl && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={() => setShowImagePreview(false)}
              className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-lg z-10"
            >
              <X size={20} className="text-gray-700" />
            </button>

            <div className="bg-white rounded-xl overflow-hidden">
              <div className="relative">
                <img
                  src={previewImageUrl}
                  alt="Paper preview"
                  className="w-full h-auto max-h-[80vh] object-contain"
                />
                <div className="absolute top-2 left-2 bg-green-500 text-white px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 shadow-md">
                  <Lock size={14} />
                  Privacy Protected - Ready for Annotation
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Annotation Modal */}
      {showAnnotationModal && selectedPaper && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col ${
            sideBySideMode ? 'w-full max-w-7xl' : 'w-full max-w-4xl'
          }`}>
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Annotate Paper - {getPaperTypeLabel(selectedPaper.paperType)}
                </h3>
                <p className="text-sm text-gray-500">
                  Paper ID: {selectedPaper.id} • {selectedPaper.totalFarmers} farmers
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* Side-by-side toggle button */}
                <button
                  onClick={() => {
                    setSideBySideMode(!sideBySideMode);
                    const imageUrl = getImageUrl(selectedPaper);
                    // Images from GlobalAnnotationQueue are already privacy-blurred
                    // Set the URL directly instead of re-processing
                    if (!sideBySideMode && !blurredImageUrl && imageUrl) {
                      setBlurredImageUrl(imageUrl);
                    }
                  }}
                  className={`hidden md:flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                    sideBySideMode
                      ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="Toggle side-by-side view"
                >
                  <Columns size={16} />
                  {sideBySideMode ? 'Close Preview' : 'Side-by-Side'}
                </button>
                {/* Download button */}
                <button
                  onClick={() => handleDownloadBlurred(getImageUrl(selectedPaper), selectedPaper.id)}
                  disabled={!getImageUrl(selectedPaper) || processingBlur}
                  className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition"
                  title="Download blurred image"
                >
                  {processingBlur ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Download size={16} />
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowAnnotationModal(false);
                    setSelectedPaper(null);
                    setSideBySideMode(false);
                    setBlurredImageUrl("");
                  }}
                  className="p-2 hover:bg-gray-200 rounded-full"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
            </div>

            {/* Modal Body - Flex container for side-by-side */}
            <div className={`flex-1 overflow-hidden flex ${sideBySideMode ? 'flex-row' : 'flex-col'}`}>
              {/* Side panel for image preview (only in side-by-side mode) */}
              {sideBySideMode && (
                <div className="w-1/2 border-r border-gray-200 overflow-hidden p-4 bg-gray-50 flex flex-col">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Eye size={16} />
                      Privacy-Protected Preview
                    </h4>
                    {/* Zoom controls */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setImageScale(Math.max(0.5, imageScale - 0.2))}
                        className="px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-xs"
                        title="Zoom Out"
                      >
                        -
                      </button>
                      <span className="text-xs text-gray-600">{Math.round(imageScale * 100)}%</span>
                      <button
                        onClick={() => setImageScale(Math.min(5, imageScale + 0.2))}
                        className="px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-xs"
                        title="Zoom In"
                      >
                        +
                      </button>
                      <button
                        onClick={handleResetZoom}
                        className="px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-xs"
                        title="Reset Zoom"
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                  {processingBlur ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center">
                        <Loader2 className="w-8 h-8 text-cyan-600 animate-spin mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Processing privacy blur...</p>
                      </div>
                    </div>
                  ) : blurredImageUrl ? (
                    <div
                      className="relative flex-1 overflow-hidden rounded-lg border border-gray-200 bg-gray-100"
                      style={{ cursor: imageScale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
                    >
                      <div
                        className="w-full h-full overflow-hidden"
                        onWheel={handleWheel}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                      >
                        <img
                          src={blurredImageUrl}
                          alt="Blurred paper preview"
                          className="select-none"
                          style={{
                            transform: `scale(${imageScale}) translate(${imagePosition.x / imageScale}px, ${imagePosition.y / imageScale}px)`,
                            transformOrigin: 'center center',
                            transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                            width: '100%',
                            height: 'auto',
                          }}
                          draggable={false}
                        />
                      </div>
                      <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1 pointer-events-none">
                        <Lock size={12} />
                        Privacy Protected
                      </div>
                      {imageScale > 1 && (
                        <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs pointer-events-none">
                          Use scroll wheel to zoom • Drag to pan
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-64 border-2 border-dashed border-gray-300 rounded-lg">
                      <div className="text-center">
                        <Image className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No preview available</p>
                        <button
                          onClick={() => {
                            const imageUrl = getImageUrl(selectedPaper);
                            // Images from GlobalAnnotationQueue are already privacy-blurred
                            // Set the URL directly instead of re-processing
                            if (imageUrl) {
                              setBlurredImageUrl(imageUrl);
                            }
                          }}
                          className="mt-2 text-cyan-600 text-sm hover:underline"
                        >
                          Load preview
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Download button in side panel */}
                  {blurredImageUrl && (
                    <button
                      onClick={() => handleDownloadBlurred(getImageUrl(selectedPaper), selectedPaper.id)}
                      className="mt-3 w-full py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition flex items-center justify-center gap-2"
                    >
                      <Download size={16} />
                      Download Blurred Image
                    </button>
                  )}
                </div>
              )}

              {/* Main annotation content */}
              <div className={`${sideBySideMode ? 'w-1/2' : 'w-full'} overflow-y-auto p-4`}>
                {loadingMappings ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-cyan-600 animate-spin" />
                  </div>
                ) : (
                  <>
                  {/* Progress indicator */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                      <span>Progress</span>
                      <span>{currentFarmerIndex + 1} of {farmerMappings.length}</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-cyan-500 transition-all duration-300"
                        style={{
                          width: `${((currentFarmerIndex + 1) / farmerMappings.length) * 100}%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Image reference */}
                  {getImageUrl(selectedPaper) && (
                    <div className="mb-4">
                      <button
                        onClick={() => handleViewImage(getImageUrl(selectedPaper))}
                        className="text-sm text-cyan-600 hover:underline flex items-center gap-1"
                      >
                        <Eye size={14} />
                        View reference image
                      </button>
                    </div>
                  )}

                  {/* Current farmer input */}
                  {currentFarmer && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="bg-cyan-100 text-cyan-700 px-2 py-1 rounded text-sm font-medium">
                            Row #{currentFarmer.rowId}
                          </span>
                          <span className="text-gray-600 blur-[8px] select-none">
                            {currentFarmer.cloudName}
                          </span>
                          <Lock size={12} className="text-gray-400" />
                        </div>
                        <p className="text-xs text-gray-500">
                          Enter milk quantity (in Kgs) for each day
                        </p>
                      </div>

                      {/* Date columns inputs */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
                        {dateColumns.map((day, dayIdx) => (
                          <div key={dayIdx}>
                            <label className="block text-xs text-gray-600 mb-1">
                              {day}
                            </label>
                            <input
                              type="number"
                              step="0.1"
                              min="0"
                              value={annotationData[currentFarmer.rowId]?.[dayIdx] || ""}
                              onChange={(e) =>
                                handleDataChange(currentFarmer.rowId, dayIdx, e.target.value)
                              }
                              placeholder="0.0"
                              className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Navigation */}
                  <div className="flex items-center justify-between mt-4">
                    <button
                      onClick={handlePrevFarmer}
                      disabled={currentFarmerIndex === 0}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
                        currentFarmerIndex === 0
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      <ChevronLeft size={18} />
                      Previous
                    </button>

                    {/* Farmer counter */}
                    <div className="flex items-center gap-2">
                      {farmerMappings.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentFarmerIndex(idx)}
                          className={`w-8 h-8 rounded-full text-xs font-medium transition ${
                            idx === currentFarmerIndex
                              ? "bg-cyan-600 text-white"
                              : Object.keys(annotationData[farmerMappings[idx]?.rowId] || {}).length > 0
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                          title={`Farmer ${idx + 1}`}
                        >
                          {idx + 1}
                        </button>
                      ))}
                    </div>

                    {currentFarmerIndex < farmerMappings.length - 1 ? (
                      <button
                        onClick={handleNextFarmer}
                        className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-700 transition"
                      >
                        Next Farmer
                        <ChevronRight size={18} />
                      </button>
                    ) : (
                      <button
                        onClick={handleSubmitAnnotation}
                        disabled={submitting}
                        className={`px-6 py-2 rounded-lg font-medium transition flex items-center gap-2 ${
                          submitting
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-green-600 text-white hover:bg-green-700"
                        }`}
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="animate-spin" size={18} />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Check size={18} />
                            Submit Annotation
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </>
              )}
              </div>
            </div>

            {/* Rate reminder */}
            <div className="p-3 bg-green-50 border-t border-green-100 text-center">
              <p className="text-sm text-green-700">
                💰 Complete this paper to earn{" "}
                <span className="font-bold">
                  KES {ANNOTATION_RATES[selectedPaper.paperType]}
                </span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Return content wrapped in appropriate layout
  return userRole ? getLayoutWrapper(content) : content;
};

export default AnnotationMarketplace;
